'use client';
import { useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';

/**
 * AuroraBackground -v4 "Dual Theme"
 *
 * Dark  : fond #060D18, lignes bleues #3590E3 -comportement original.
 * Light : fond transparent (gradient CSS géré ailleurs),
 *         lignes bleues #4a90d9 ultra-discrètes sur le blanc.
 */
export default function AuroraBackground() {
  const canvasRef  = useRef(null);
  const { theme }  = useTheme();
  const isLight    = theme === 'light';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf;

    const W = () => canvas.width;
    const H = () => canvas.height;

    /* ── Config selon thème ── */
    const CELL       = 52;
    const SCAN_SPEED = 0.25;
    const SCAN_WIDTH = 260;

    // Dark
    const DARK = {
      bg:        '#060D18',
      blue:      [53, 144, 227],
      lineBase:  0.04,
      linePeak:  0.22,
      scanPeak:  0.045,
      vigAlpha:  0.62,
    };

    // Light : lignes à peine perceptibles, pas de fond opaque
    const LIGHT = {
      bg:        null,          // pas de remplissage -le CSS gradient s'en charge
      blue:      [74, 144, 217],
      lineBase:  0.025,
      linePeak:  0.09,
      scanPeak:  0.018,
      vigAlpha:  0.0,           // pas de vignette sombre en mode clair
    };

    const CFG = isLight ? LIGHT : DARK;
    const [R, G, B] = CFG.blue;

    /* ── État ── */
    let hLines = [];
    let vLines = [];
    let scanX  = 0;

    const build = () => {
      hLines = [];
      vLines = [];
      const rows = Math.ceil(H() / CELL) + 1;
      const cols = Math.ceil(W() / CELL) + 1;
      for (let r = 0; r <= rows; r++)
        hLines.push({ y: r * CELL, phase: Math.random() * Math.PI * 2 });
      for (let c = 0; c <= cols; c++)
        vLines.push({ x: c * CELL, phase: Math.random() * Math.PI * 2 });
      scanX = 0;
    };

    const resize = () => {
      canvas.width  = canvas.offsetWidth  || window.innerWidth;
      canvas.height = canvas.offsetHeight || window.innerHeight;
      build();
    };

    /* ── Draw ── */
    let frame = 0;
    const draw = () => {
      frame++;
      ctx.clearRect(0, 0, W(), H());

      /* Fond -seulement en dark */
      if (CFG.bg) {
        ctx.fillStyle = CFG.bg;
        ctx.fillRect(0, 0, W(), H());
      }

      /* ── Lignes horizontales ── */
      hLines.forEach(line => {
        const pulse     = 0.5 + 0.5 * Math.sin(frame * 0.008 + line.phase);
        const scanDist  = Math.abs(scanX - W() * 0.5);
        const scanBoost = Math.max(0, 1 - scanDist / (SCAN_WIDTH * 0.8)) * (isLight ? 0.03 : 0.12);
        const alpha     = CFG.lineBase + pulse * (isLight ? 0.015 : 0.04) + scanBoost;

        ctx.beginPath();
        ctx.moveTo(0, line.y);
        ctx.lineTo(W(), line.y);
        ctx.strokeStyle = `rgba(${R},${G},${B},${alpha})`;
        ctx.lineWidth   = 0.5;
        ctx.stroke();
      });

      /* ── Lignes verticales ── */
      vLines.forEach(line => {
        const dist  = Math.abs(line.x - scanX);
        const boost = Math.max(0, 1 - dist / SCAN_WIDTH);
        const pulse = 0.5 + 0.5 * Math.sin(frame * 0.009 + line.phase);
        const alpha = CFG.lineBase + pulse * (isLight ? 0.01 : 0.03) + boost * (CFG.linePeak - CFG.lineBase);

        const grad = ctx.createLinearGradient(line.x, 0, line.x, H());
        grad.addColorStop(0,    `rgba(${R},${G},${B},0)`);
        grad.addColorStop(0.15, `rgba(${R},${G},${B},${alpha})`);
        grad.addColorStop(0.85, `rgba(${R},${G},${B},${alpha})`);
        grad.addColorStop(1,    `rgba(${R},${G},${B},0)`);

        ctx.beginPath();
        ctx.moveTo(line.x, 0);
        ctx.lineTo(line.x, H());
        ctx.strokeStyle = grad;
        ctx.lineWidth   = 0.5;
        ctx.stroke();
      });

      /* ── Halo du scan ── */
      const halo = ctx.createLinearGradient(scanX - SCAN_WIDTH, 0, scanX + SCAN_WIDTH, 0);
      halo.addColorStop(0,    `rgba(${R},${G},${B},0)`);
      halo.addColorStop(0.42, `rgba(${R},${G},${B},${CFG.scanPeak * 0.55})`);
      halo.addColorStop(0.5,  `rgba(${R},${G},${B},${CFG.scanPeak})`);
      halo.addColorStop(0.58, `rgba(${R},${G},${B},${CFG.scanPeak * 0.55})`);
      halo.addColorStop(1,    `rgba(${R},${G},${B},0)`);
      ctx.fillStyle = halo;
      ctx.fillRect(scanX - SCAN_WIDTH, 0, SCAN_WIDTH * 2, H());

      /* ── Avancer le scan ── */
      scanX += SCAN_SPEED;
      if (scanX > W() + SCAN_WIDTH) scanX = -SCAN_WIDTH;

      /* ── Vignette bords (dark uniquement) ── */
      if (CFG.vigAlpha > 0) {
        const vig = ctx.createRadialGradient(W()/2, H()/2, H() * 0.25, W()/2, H()/2, H() * 0.9);
        vig.addColorStop(0, 'rgba(0,0,0,0)');
        vig.addColorStop(1, `rgba(0,0,0,${CFG.vigAlpha})`);
        ctx.fillStyle = vig;
        ctx.fillRect(0, 0, W(), H());
      }

      raf = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener('resize', resize);
    draw();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(raf);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLight]); // redémarre quand le thème change

  return (
    <canvas
      ref={canvasRef}
      style={{
        position:      'absolute',
        inset:          0,
        width:         '100%',
        height:        '100%',
        pointerEvents: 'none',
        display:       'block',
        // En light, le canvas est transparent → le gradient CSS est visible dessous
        opacity:       isLight ? 0.7 : 1,
      }}
    />
  );
}